import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { equipmentApi, type EquipmentFile } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';

/**
 * Files on an equipment request — the rental agreement, mainly (#571) — for the
 * edit drawer.
 *
 * Stored in the tracker itself, not in the job's SharePoint Job Folder, so it
 * works on requests with no job. Only people who can edit equipment requests
 * (and Admin) can open them, since an agreement shows the rental rate. The API
 * enforces that; the drawer only renders this section for them
 * (rentalTermsAccess.ts).
 *
 * Files go in through a drop box: drag one or more onto it, or click it to pick
 * them. Several upload one after another, and any that can't go are named.
 *
 * Styled inline like the rest of the drawer, so it depends on no other module's
 * stylesheet being mounted (styles/cssCoPresence.spec.ts).
 */

/** Mirrors EQUIPMENT_FILE_MAX_BYTES in the API. Checked here only to say so before uploading. */
export const MAX_FILE_BYTES = 15 * 1024 * 1024;

/** What the file picker offers. The API's allowlist is the real check. */
export const ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv';

/** Why `file` can't be uploaded, or null if it can. */
export function uploadProblem(file: { size: number }): string | null {
  if (file.size > MAX_FILE_BYTES) return 'That file is larger than the 15 MB limit.';
  if (file.size === 0) return 'That file is empty.';
  return null;
}

/**
 * Split a batch into the files that can upload and a message for each that
 * can't, named, so dropping five files and losing one says which one.
 */
export function sortUploads<F extends { name: string; size: number }>(files: F[]): { ok: F[]; problems: string[] } {
  const ok: F[] = [];
  const problems: string[] = [];
  for (const f of files) {
    const p = uploadProblem(f);
    if (p) problems.push(`“${f.name}”: ${p}`);
    else ok.push(f);
  }
  return { ok, problems };
}

/**
 * Whether a drag carries files from the computer, rather than selected text or
 * a link. Array.from, because older browsers hand back a DOMStringList, which
 * has no includes().
 */
export function carriesFiles(dt: { types?: ArrayLike<string> | null } | null | undefined): boolean {
  return Array.from(dt?.types ?? []).includes('Files');
}

export function fmtSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const muted: React.CSSProperties = { fontSize: 12, color: 'var(--gray)', margin: 0 };
const errorBox: React.CSSProperties = {
  padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12,
};

export default function EquipmentFiles({ equipmentId }: { equipmentId: number }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /** "2 of 3" while a batch uploads, '' for a single file, else null. */
  const [progress, setProgress] = useState<string | null>(null);
  // Set synchronously, unlike state: a second drop that lands before React
  // re-renders must still see that a batch is already running.
  const runningRef = useRef(false);
  // A STRING second segment, like ['equipment', 'owned-units']. The project
  // tabs key on ['equipment', projectId], and a number here would let their
  // prefix invalidations match a request whose id equals that project id.
  const key = ['equipment', 'files', equipmentId];

  const listQuery = useQuery({
    queryKey: key,
    queryFn: () => equipmentApi.listFiles(equipmentId),
  });

  const upload = useMutation({
    mutationFn: (file: File) => equipmentApi.uploadFile(equipmentId, file),
  });
  const busy = upload.isPending || progress !== null;
  const blocked = busy || listQuery.isError;

  /** Upload a batch one file at a time, reporting every file that didn't go. */
  const uploadAll = async (picked: File[]) => {
    if (picked.length === 0 || blocked || runningRef.current) return;
    runningRef.current = true;
    // The last batch's errors are about other files; clear them as this starts.
    setProblem(null);
    const { ok, problems } = sortUploads(picked);
    try {
      for (let i = 0; i < ok.length; i++) {
        setProgress(ok.length > 1 ? `${i + 1} of ${ok.length}` : '');
        try {
          await upload.mutateAsync(ok[i]);
        } catch (err) {
          problems.push(`“${ok[i].name}”: ${(err as Error).message || 'Upload failed.'}`);
        }
      }
    } finally {
      runningRef.current = false;
      setProgress(null);
    }
    setProblem(problems.length ? problems.join('\n') : null);
    qc.invalidateQueries({ queryKey: key });
  };

  // A file dropped just OUTSIDE the box would make the browser open it in this
  // tab, navigating away and losing the drawer. While this section is on
  // screen, the window ignores file drops; the box handles its own.
  useEffect(() => {
    const stop = (e: DragEvent) => {
      if (carriesFiles(e.dataTransfer)) e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);

  const remove = useMutation({
    mutationFn: (fileId: number) => equipmentApi.removeFile(equipmentId, fileId),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
    onError: (err) => setProblem((err as Error).message || 'Couldn’t remove that file.'),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Reset so picking the same file again re-fires onChange.
    if (inputRef.current) inputRef.current.value = '';
    void uploadAll(picked);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void uploadAll(Array.from(e.dataTransfer.files ?? []));
  };

  // Same approach as the feedback attachments: fetch the bytes, then save them
  // under their own name. Never opened in a tab, so nothing a file contains
  // runs in the tracker's origin.
  const download = async (f: EquipmentFile) => {
    setProblem(null);
    try {
      const blob = await equipmentApi.downloadFile(equipmentId, f.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setProblem(`Couldn’t download “${f.fileName}”.`);
    }
  };

  const files = listQuery.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={muted}>
        Kept in the Project Tracker, not the Job Folder, and visible only to people who can edit equipment requests.
      </p>

      <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={onPick} />
      {/* A real button, so the keyboard and screen readers can use it too:
          Enter or Space opens the file picker, exactly like a click. */}
      <button
        type="button"
        data-testid="equipment-drop-zone"
        onClick={() => inputRef.current?.click()}
        // Only a drag carrying FILES lights the box. Selected text or a link
        // dragged over it would drop to nothing, so it isn't invited.
        onDragEnter={(e) => {
          if (!carriesFiles(e.dataTransfer)) return;
          e.preventDefault();
          if (!blocked) setDragging(true);
        }}
        onDragOver={(e) => {
          if (!carriesFiles(e.dataTransfer)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = blocked ? 'none' : 'copy';
        }}
        // The text inside ignores the pointer (pointerEvents: 'none'), so a
        // drag over it never leaves the button. Relying on relatedTarget
        // instead flickers where browsers leave it null on drag events.
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        disabled={blocked}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: '100%',
          padding: '18px 12px', borderRadius: 'var(--ew-r-md)', cursor: blocked ? 'default' : 'pointer',
          border: `2px dashed ${dragging ? 'var(--ew)' : 'var(--border)'}`,
          background: dragging ? '#E6F2EB' : 'var(--surface)',
          color: 'var(--text)', fontFamily: 'inherit', opacity: blocked && !busy ? 0.6 : 1,
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ew)', pointerEvents: 'none' }}>
          {busy
            ? (progress ? `Uploading ${progress}…` : 'Uploading…')
            : dragging ? 'Drop to attach' : 'Drag files here, or click to choose'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--gray)', pointerEvents: 'none' }}>PDF, Office, images or text, up to 15 MB each</span>
      </button>

      {problem && <div style={{ ...errorBox, whiteSpace: 'pre-line' }}>{problem}</div>}

      {listQuery.isPending ? (
        <p style={muted}>Loading files…</p>
      ) : listQuery.isError ? (
        <div style={errorBox}>{(listQuery.error as Error).message}</div>
      ) : files.length === 0 ? (
        <p style={muted}>No files yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map((f) => {
            const who = f.uploadedBy?.displayName ?? f.uploadedBy?.email ?? 'Unknown';
            const size = fmtSize(f.sizeBytes);
            return (
              <li key={f.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)' }}>
                <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    type="button"
                    onClick={() => download(f)}
                    title="Download"
                    style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', color: 'var(--ew)', fontWeight: 600, fontSize: 13, wordBreak: 'break-all' }}
                  >
                    {f.fileName}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                    {who} · {fmtDateTime(f.uploadedAt)}{size ? ` · ${size}` : ''}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${f.fileName}`}
                  title="Remove this file"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove “${f.fileName}” from this request?`)) {
                      setProblem(null);
                      remove.mutate(f.id);
                    }
                  }}
                  style={{ flex: '0 0 auto', background: 'none', border: 0, cursor: 'pointer', color: 'var(--gray)', fontSize: 16, lineHeight: 1, padding: '2px 6px' }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
